import InboxBase from '@/dealers/components/Inbox'
import { Conversation, ConversationScheduledMessage } from '@/dealers/components/Inbox/types'
import Page from '@/gf/components/Page'
import SearchInput from '@/gf/components/SearchInput'
import { trackSearch } from '@/gf/modules/Analytics'
import Id from '@/gf/modules/Id'
import { Maybe } from '@/types'
import debounce from 'lodash/debounce'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { NumberParam, StringParam, useQueryParams, withDefault } from 'use-query-params'
import {
  ConversationMessagesDocument,
  ConversationsDocument,
  ConversationsQuery,
  useCreateConversationMutation,
  useCreateMessageMutation,
  useReadReceiptsQuery,
  useRequestScheduledMessagesLazyQuery,
  useUnreadMessagesQuery,
} from '../_gen/gql'
import Frame from '../components/Frame'
import useConversations from '../hooks/useConversations'
import useMessagesForConversation from '../hooks/useMessagesForConversation'
import ConversationM from '../modules/Conversation'
import RequestForQuoteM from '../modules/RequestForQuote'
import StoreOrderM from '../modules/StoreOrder'
import Urls from '../modules/Urls'
import InboxDetails from './Inbox/InboxDetails'
import ScopeAction from '@/gf/components/ScopeAction'
import FilterSet from '@/gf/modules/FilterSet'

const displayCustomerContactInfo = (
  conversation: ConversationsQuery['conversations']['entries'][number]
) =>
  conversation.requestForQuote
    ? RequestForQuoteM.canDisplayCustomerContactInfo({
        step: conversation.requestForQuote.step,
        user: conversation.requestForQuote.user && {
          isCustomerContact: conversation.requestForQuote.user.isCustomerContact,
        },
        storeOrder: conversation.requestForQuote.quote
          ? {
              state: conversation.requestForQuote.quote.state,
            }
          : null,
      })
    : conversation.storeOrder
      ? StoreOrderM.canDisplayCustomerContactInfo({
          state: conversation.storeOrder.state,
          user: { isCustomerContact: conversation.storeOrder.customer.isCustomerContact },
        })
      : false

const inboxTitle = (conversation: ConversationsQuery['conversations']['entries'][number]) =>
  conversation.requestForQuote
    ? [
        `Request ${Id.shorten(conversation.requestForQuote.id)}`,
        ...(conversation.requestForQuote.quote
          ? [`Order ${Id.shorten(conversation.requestForQuote.quote.id)}`]
          : []),
      ]
    : conversation.storeOrder
      ? `Order ${Id.shorten(conversation.storeOrder.id)}`
      : ''

const conversationName = (conversation: ConversationsQuery['conversations']['entries'][number]) =>
  conversation.admin
    ? ConversationM.ADMIN_CONVERSATION_NAME
    : conversation.requestForQuote
      ? ConversationM.conversationNameForRfq(
          conversation.requestForQuote,
          RequestForQuoteM.canDisplayCustomerContactInfo({
            step: conversation.requestForQuote.step,
            user: conversation.requestForQuote.user && {
              isCustomerContact: conversation.requestForQuote.user.isCustomerContact,
            },
            storeOrder: conversation.requestForQuote.quote
              ? {
                  state: conversation.requestForQuote.quote.state,
                }
              : null,
          })
        )
      : conversation.storeOrder
        ? ConversationM.conversationNameForStoreOrder(
            conversation.storeOrder,
            StoreOrderM.canDisplayCustomerContactInfo({
              state: conversation.storeOrder.state,
              user: { isCustomerContact: conversation.storeOrder.customer.isCustomerContact },
            })
          )
        : ''

const Inbox = () => {
  const navigate = useNavigate()
  const { conversationId } = useParams()
  const [scheduledMessage, setScheduledMessage] =
    useState<Maybe<ConversationScheduledMessage>>(null)

  const location = useLocation()
  const [{ page, search: searchTerm, scope }, setQuery] = useQueryParams({
    page: withDefault(NumberParam, 1),
    search: withDefault(StringParam, ''),
    scope: StringParam,
  })
  const setPage = (newPage: number) => setQuery({ page: newPage })
  const [fetchScheduledMessages] = useRequestScheduledMessagesLazyQuery()
  const { refetch: refetchUnreadMessages } = useUnreadMessagesQuery()

  // Don't show a spinner every time we re-run the query
  const [conversationsData, setConversationsData] = useState<
    ConversationsQuery['conversations']['entries'] | undefined
  >()

  const {
    data,
    loading,
    refetch: refetchConversations,
  } = useConversations({
    filters: [
      ...(searchTerm ? [['contains', 'search', searchTerm]] : []),
      ...(scope === 'unread'
        ? FilterSet.toApiFilters([ConversationM.unreadMessagesConversationFilter()])
        : []),
    ],
    // When viewing your UNREAD messages, we need to keep showing the selected conversation
    // Otherwise, the conversation will disappear when you select it (because it's no longer unread)
    includeConversationId: conversationId ?? null,
    page,
    pageSize: 30,
    onCompleted: (resultData) => {
      if (searchTerm) {
        trackSearch(searchTerm, 'inbox_page', {
          resultsFound: resultData.conversations.pagination.totalResults,
        })
      }
      setConversationsData(resultData.conversations.entries)
    },
  })

  const conversationsById = useMemo(() => {
    if (!conversationsData) return {}

    return conversationsData.reduce(
      (all, conversation) => ({ ...all, [conversation.id]: conversation }),
      {}
    )
  }, [conversationsData])

  const [createMessage] = useCreateMessageMutation({
    refetchQueries: [ConversationMessagesDocument, ConversationsDocument],
  })

  const [createConversation] = useCreateConversationMutation({
    refetchQueries: [ConversationsDocument],
  })

  const onSelectedConversationIdChange = (selectedConversationId: string | undefined) => {
    return selectedConversationId
      ? navigate(`${Urls.inboxConversation(selectedConversationId)}${location.search}`)
      : navigate(`${Urls.inbox()}${location.search}`)
  }

  const createInboxDetails = useCallback((props) => <InboxDetails {...props} />, [])

  const onSearchChanged = useMemo(
    () =>
      debounce((newSearch: string, currentScope: string | null | undefined) => {
        // Switch to the scope showing all messages
        const scope = newSearch ? undefined : currentScope
        setQuery({ search: newSearch, scope, page: 1 })
      }, 300),
    []
  )

  const onScopeChanged = (newScope: string | null | undefined) => {
    // Deselect a conversation if one is selected
    const params: Record<string, string> = {
      page: '1',
    }

    if (newScope !== null && newScope !== undefined) {
      params.scope = newScope
    }

    if (searchTerm) {
      params.search = searchTerm
    }

    navigate(`${Urls.inbox()}?${new URLSearchParams(params).toString()}`)
  }

  const fetchScheduledMessageForConversation = async (requestForQuoteId) => {
    const result = await fetchScheduledMessages({ variables: { requestForQuoteId } })

    setScheduledMessage(
      result?.data?.scheduledMessages
        .filter((sm) => !sm.cancelledAt && !sm.sentAt)
        .map((sm) => ({
          ...sm,
          viewMorePath: Urls.requestForQuoteAutomations(requestForQuoteId),
        }))[0] ?? null
    )
  }

  useEffect(() => {
    if (conversationId && conversationsData) {
      setScheduledMessage(null)

      const conversation = conversationsData.find((c) => c.id === conversationId)

      if (conversation?.requestForQuote?.id) {
        fetchScheduledMessageForConversation(conversation?.requestForQuote?.id)
      }
    }
  }, [conversationsData, conversationId])

  const conversations = useMemo(
    () =>
      conversationsData?.map(
        (c) =>
          ({
            id: c.id,
            admin: c.admin,
            name: conversationName(c),
            lastMessage: c.lastMessage,
            unreadMessages: c.unreadMessages,
            storeId: c.store?.id,
            source: c.storeOrder
              ? {
                  type: 'store_order',
                  id: c.storeOrder.id,
                  user: c.storeOrder.customer,
                }
              : c.requestForQuote && {
                  type: 'request',
                  id: c.requestForQuote.id,
                  user: c.requestForQuote.user,
                },
            scheduledMessage: c.id === conversationId ? scheduledMessage : null,
          }) as Conversation
      ),
    [conversationsData, conversationId, scheduledMessage]
  )

  return (
    <Frame>
      <Page title="Inbox">
        <div className="mt-4 space-y-2">
          <div className="flex gap-2">
            <div className="rounded-md shadow-sm text-sm border-1 overflow-hidden">
              <ScopeAction selected={!scope} onClick={() => onScopeChanged(undefined)}>
                All
              </ScopeAction>
              <ScopeAction selected={scope === 'unread'} onClick={() => onScopeChanged('unread')}>
                Unread
              </ScopeAction>
            </div>

            <div className="grow">
              <SearchInput
                value={searchTerm}
                onChange={(newSearch) => onSearchChanged(newSearch, scope)}
                placeholder="filter conversations by order id, request id or message content"
                loading={loading}
              />
            </div>
          </div>

          {searchTerm.length > 0 && conversations?.length === 0 && (
            <div className="prose text-sm">No messages found 🫤</div>
          )}
        </div>

        <InboxBase
          className="mt-4"
          conversations={conversations}
          selectedConversationId={conversationId}
          onSelectedConversationIdChange={onSelectedConversationIdChange}
          useMessagesForConversation={useMessagesForConversation}
          useReadReceiptsForConversation={useReadReceiptsQuery}
          refetchConversations={() =>
            Promise.all([refetchConversations(), refetchUnreadMessages()])
          }
          page={page}
          setPage={setPage}
          pagination={data?.pagination ?? { totalPages: 0, totalResults: 0 }}
          newConversations={[]}
          DetailsForConversation={createInboxDetails}
          displayCustomerContactInfo={(conversation) =>
            displayCustomerContactInfo(conversationsById[conversation.id])
          }
          conversationRowContent={(conversation) => ({
            name: inboxTitle(conversationsById[conversation.id]),
            details: { type: conversation.admin ? 'admin' : 'buyer', name: conversation.name },
          })}
          onCreateMessage={({ messageInput }) =>
            createMessage({
              variables: {
                conversationId: messageInput.conversationId,
                text: messageInput.text,
                attachmentUrls: messageInput.attachmentUrls ?? [],
              },
            })
          }
          onCreateConversation={async ({ conversationInput, messageText, attachmentUrls }) => {
            const { data: createConvoData } = await createConversation({
              variables: {
                conversation: {
                  requestForQuoteId: conversationInput.requestForQuoteId ?? null,
                  storeOrderId: conversationInput.storeOrderId ?? null,
                  storeId: conversationInput.storeId as string,
                  admin: conversationInput.admin,
                },
                attachmentUrls: attachmentUrls ?? null,
                messageText: messageText ?? null,
              },
            })

            return createConvoData?.createConversation
          }}
        />
      </Page>
    </Frame>
  )
}

export default Inbox
